/**
 * WHAT KIND OF PLACE THE CUSTOMER IS.
 *
 * A copy of `ORGANIZATION_TYPES` in `functions/src/intake/intakeShape.ts`,
 * because the web app cannot import from `functions/`. Held in step by
 * `organizationTypes.guard.test.ts`, which reads the backend source — the same
 * arrangement `relationshipMatchesBackend.guard.test.ts` makes for the contact
 * roles, and for the same reason: a key here that the backend does not know is
 * refused by the zod enum, and the customer sees a dropdown that will not save.
 *
 * The list itself is not invented. It is what the marketing site already
 * welcomes, verbatim — "Clinics, surgery centers, medical groups, behavioral
 * health, home health, hospice, long-term care and hospitals"
 * (`site/src/pages/Demo.tsx`) — so the form cannot offer somebody a category
 * the product has never claimed to serve.
 */
/**
 * WHY NO LABEL HERE IS A PLAN NAME.
 *
 * The plans are `clinic`, `professional`, `organization` and `custom`
 * (`billing/plans.ts`), and the plan is decided by LOCATION COUNT, not by what
 * kind of place this is: a clinic with eight sites is on the Organization plan.
 * If the form offered "Clinic" as an organization type, a customer could pick
 * it, receive an order form reading "Organization — $5,995", and reasonably
 * believe they had chosen a tier and been moved off it. That is the negotiation
 * running backwards that this whole form is built to avoid, so the labels are
 * deliberately worded away from the plan vocabulary and a guard test holds
 * them there.
 */
export const ORGANIZATION_TYPES = [
  { key: 'hospital', label: 'Hospital or health system' },
  { key: 'clinic', label: 'Outpatient clinic' },
  { key: 'surgeryCenter', label: 'Surgery center' },
  { key: 'medicalGroup', label: 'Medical group' },
  { key: 'behavioralHealth', label: 'Behavioral health' },
  { key: 'homeHealth', label: 'Home health' },
  { key: 'hospice', label: 'Hospice' },
  { key: 'longTermCare', label: 'Long-term care' },
  { key: 'other', label: 'Something else' },
] as const;

export type OrganizationType = (typeof ORGANIZATION_TYPES)[number]['key'];

/** The label for a stored key, or null for one we do not know. */
export const organizationTypeLabel = (key: string | null | undefined): string | null =>
  ORGANIZATION_TYPES.find((t) => t.key === key)?.label ?? null;
